import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/styles';
import {
  Checkbox,
  CircularProgress,
  FormControl,
  InputBase,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';

import { selectProjects } from '../../reducers/projectSlice';
import { loadFBA, selectFBA, updateFBA } from '../../reducers/fbaSlice';
import { selectUser } from '../../reducers/userSlice';

import { FBAAnalyticsChart } from './FBAAnalyticsChart.js';

export const FBAAnalytics = ({ darkMode, fbaUnits = 'field' }) => {
  const theme = useTheme();

  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];
  const fba = useSelector(selectFBA);

  let fbaIds = fba?.allIds?.length > 0 ? fba?.allIds : [];
  fbaIds = fbaIds.filter(id => fba?.byId?.[id]?.projectId === currentProject?.id || !fba?.byId?.[id]?.projectId || fba?.byId?.[id]?.public); // filter by current project and public fba

  const [displayFBACases, setDisplayFBACases] = useState(fba?.currentFBAId ? [fba?.currentFBAId] : []);
  const [isLoadingFBAData, setIsLoadingFBAData] = useState(false);
  const [normalizeRate, setNormalizeRate] = useState(1);

  return (
    <Paper
      elevation={0}
      sx={{ boxShadow: 1 }}
      style={{
        flexBasis: 0,
        flexShrink: 1,
        flexGrow: 1,
        margin: 10,
        padding: 10,
        backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">FBA Analytics</Typography>
      </div>

      <div
        style={{
          marginTop: 10,
          marginLeft: 20,
          width: 'calc(100% - 40px)',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between'
        }}
      >
        <FormControl style={{ marginRight: 10, marginBottom: 5 }}>
          <Typography variant="subtitle2" color="textSecondary" style={{ fontWeight: 'bold', fontSize: 10 }}>
            RATE NORMALIZATION
          </Typography>
          <ToggleButtonGroup
            value={normalizeRate}
            onChange={(e, newVal) => newVal > 0 && setNormalizeRate(newVal)}
            size="small"
            style={{ height: 30, marginTop: 0 }}
            exclusive
          >
            <ToggleButton value={1} fontSize="small" color={darkMode ? '' : 'primary'} style={{ height: 20 }}>
              <Typography style={{ fontSize: 10 }}>Off</Typography>
            </ToggleButton>
            <ToggleButton value={2} fontSize="small" color={darkMode ? '' : 'primary'} style={{ height: 20 }}>
              <Typography style={{ fontSize: 10 }}>Pressure</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Typography fontSize="12px">Select FBA Cases:</Typography>
          <FormControl>
            <Select
              value={fbaIds}
              size="small"
              multiple
              renderValue={() => fba?.byId?.[fba?.currentFBAId]?.name?.substring(0, 20)}
              // renderValue={(selected) => selected?.map(id => fba?.byId?.[id]?.name?.substring(0, 16))?.join(', ')}
              style={{
                width: 200,
                // height: 28,
                backgroundColor: !darkMode && '#fff',
                fontSize: '12px',
                marginLeft: 10
              }}
            >
              {fbaIds?.map(fbaId => (
                <div key={fbaId}>
                  <MenuItem value={fbaId} style={{ color: !fba?.byId?.[fbaId]?.fbaData && 'grey' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%'
                      }}
                      onClick={() => handleFBACaseClick(fbaId)}
                    >
                      <Checkbox checked={displayFBACases.indexOf(fbaId) > -1} />
                      <ListItemText primary={fba?.byId?.[fbaId]?.name?.substring(0, 20)} />
                    </div>
                    <InputBase
                      type="color"
                      value={fba?.byId?.[fbaId]?.color}
                      style={{ width: 20, height: 20, marginLeft: 10, zIndex: 999, cursor: 'pointer' }}
                      onMouseDown={e => e.stopPropagation()}
                      onChange={e => handleColorChange(e, fbaId)}
                    />
                  </MenuItem>
                </div>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div style={{ height: 1080, width: '100%', position: 'relative' }}>
        <FBAAnalyticsChart displayFBACases={displayFBACases} fbaUnits={fbaUnits} normalizeRate={normalizeRate} />

        {isLoadingFBAData && (
          <div style={{ position: 'absolute', top: '50%', left: '50%' }}>
            <CircularProgress />
          </div>
        )}
      </div>
    </Paper>
  );

  function handleColorChange(e, fbaId) {
    e.stopPropagation();
    e.preventDefault();

    if (e.target.value) {
      dispatch(updateFBA({ fbaId, fba: { ...fba.byId?.[fbaId], color: e.target.value } }));
    }
  }

  function handleFBACaseClick(fbaId) {
    if (displayFBACases?.indexOf(fbaId) === -1) {
      setDisplayFBACases(prev => [...prev, fbaId]);

      if (!fba?.byId?.[fbaId]?.fbaData) {
        handleLoadFBA(fbaId, user?.value?.email);
      }
    } else {
      setDisplayFBACases(prev => prev.filter(p => p !== fbaId));
    }
  }

  function handleLoadFBA(fbaId, email) {
    try {
      console.log('Loading FBA');

      setIsLoadingFBAData(true);

      const location = `https://ssd-fbas.s3.us-east-2.amazonaws.com/${fbaId}.json`; // TEMPORARY FIX

      // add loading status here
      dispatch(loadFBA({ location: location, keepId: fbaId, email: email, loadOnly: true })).then(() => setIsLoadingFBAData(false));
    } catch (error) {
      console.log(error);
    }
  }
};
